<script setup lang="ts">
type StudentUserData = App.Auth.Data.StudentUserData;
type UserData = App.Auth.Data.UserData;
type FaqData = App.StudentApp.Data.FaqData;

import type { Chat } from '@/api/ChatApi';
import { indexChats } from '@/api/ChatApi';

import { useAiChat } from '@/domains/student-app/composables/useAiChat';
import { useChatBot } from '@/domains/chat/composables/useChatBot';

import { formatDateTime } from '@/utils/date';

import CardFooter from '@/components/studentParentApp/card/CardFooter.vue';
import CardHeader from '@/components/studentParentApp/card/CardHeader.vue';
import CardList from '@/components/studentParentApp/card/CardList.vue';
import CardListItem from '@/components/studentParentApp/card/CardListItem.vue';
import CardTitleHeader from '@/components/studentParentApp/card/CardTitleHeader.vue';
import PageContainer from '@/components/studentParentApp/container/PageContainer.vue';
import PageSection from '@/components/studentParentApp/PageSection/PageSection.vue';
import AiAdviserDialog from '@/domains/student-app/components/adviser/AiAdviserDialog.vue';

import AdviserSolid from '@/icons/solid/adviser.svg';
import CommentData from '@/icons/line/comment-data.svg';
import CommentMessage from '@/icons/line/comment-message.svg';
import Comments from '@/icons/line/comments.svg';
import Envelope from '@/icons/line/envelope.svg';
import Link from '@/icons/line/link.svg';

const props = defineProps<{
  user: StudentUserData;
  adviser: UserData;
  faqs: FaqData[];
  chats: Chat[];
}>();

const { chatBotName, enableChatBotForStudents } = useChatBot();

const { openChat, createChat } = useAiChat();

// Create reactive chats that we can update
const reactiveChats = ref<Chat[]>(props.chats);

// Set up refresh callback to update chats when dialog closes
const refreshChats = async () => {
  try {
    const { chats: freshChats } = await indexChats();
    reactiveChats.value = freshChats;
  } catch (error) {
    console.error('Error refreshing chats:', error);
  }
};

const groupedFaqs = computed(() => {
  const groups = props.faqs.reduce(
    (acc, faq) => {
      (acc[faq.question_type] ||= []).push(faq);
      return acc;
    },
    {} as Record<string, FaqData[]>
  );

  return Object.entries(groups).map(([title, questions]) => ({
    id: title.toLowerCase().replace(/\s+/g, '-'),
    title,
    questions
  }));
});
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <PageContainer>
    <div class="flex flex-col gap-y-3 pb-5">
      <CardList headerPadding="loose">
        <template #header>
          <CardHeader
            :hasIcon="false"
            :subtitle="user.school?.name"
            :title="adviser.name"
            :user="adviser"
            badgeLabel="Adviser"
          />
        </template>
        <CardListItem
          :href="adviser?.sms_phone_number ? `sms://${adviser?.sms_phone_number}` : undefined"
          :icon="CommentData"
          :value="adviser?.sms_formatted_phone_number ?? '--'"
          isExternalLink
          label="SMS"
        />
        <CardListItem
          :href="adviser?.email ? `mailto:${adviser?.email}` : undefined"
          :icon="Envelope"
          :value="adviser?.email ?? '--'"
          isExternalLink
          label="Email"
        />
        <CardListItem
          :href="adviser?.url ?? undefined"
          :icon="Link"
          :value="adviser?.url ?? '--'"
          isExternalLink
          label="Adviser contact link"
        />
      </CardList>
    </div>
  </PageContainer>
  <PageSection v-if="enableChatBotForStudents">
    <CardList :hasContent="!!reactiveChats.length" headerPadding="loose">
      <template #header>
        <CardHeader
          :hasIcon="false"
          subtitle="College Advising Student Helper"
          :title="chatBotName"
          badgeLabel="AI assistant"
        >
          <template #avatar>
            <AdviserSolid class="size-6 text-primary-800" />
          </template>
        </CardHeader>
      </template>
      <div v-if="!!reactiveChats.length" class="max-h-72 divide-y divide-slate-200 overflow-auto">
        <CardListItem
          v-for="chat in reactiveChats"
          :badgeLabel="formatDateTime(chat.updated_at ?? null) ?? undefined"
          :key="chat.id"
          :value="chat.title ?? undefined"
          :icon="CommentMessage"
          @click="openChat(chat.id)"
          isButton
        />
      </div>
      <template #footer>
        <CardFooter
          @click="createChat()"
          :buttonIcon="Comments"
          :buttonLabel="`Have a question? Ask ${chatBotName}.`"
        />
      </template>
    </CardList>
  </PageSection>
  <PageSection v-if="enableChatBotForStudents" title="Frequently asked questions">
    <div class="flex flex-col gap-y-3">
      <CardList v-for="faq in groupedFaqs" :key="faq.id">
        <template #header>
          <CardTitleHeader :title="faq.title" />
        </template>
        <CardListItem
          v-for="question in faq.questions"
          :key="question.id"
          isButton
          @click="
            () =>
              createChat(
                `Hi ${chatBotName}. I have a question about ${faq.title}. ${question.question}${
                  question.question.endsWith('?') ? '' : '?'
                }`
              )
          "
        >
          {{ question.question }}
        </CardListItem>
        <CardListItem
          isButton
          @click="createChat(`Hi ${chatBotName}. I have a question about ${faq.title}. `)"
        >
          <span class="font-bold text-primary-700">Ask a question about {{ faq.title }}...</span>
        </CardListItem>
      </CardList>
    </div>
  </PageSection>
  <AiAdviserDialog @close="refreshChats" />
</template>
